import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function Hero() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section className="relative pt-20 md:pt-24 pb-16 md:pb-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-background-100/80 via-background-50 to-accent-50/30"></div>

      <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-center">
          <div className="w-full lg:w-[35%] animate-fade-in-up">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {t("hero.title")}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6 max-w-md">
              {t("hero.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  navigate("/artykul/usfans-spreadsheet-przewodnik");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("hero.ctaGuide")}
                <i className="ri-arrow-right-up-line"></i>
              </button>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t("hero.ctaCompare")}
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-xs text-foreground-600">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-accent-500 text-sm"></i>
                </div>
                {t("trust.updated")}
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground-600">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-accent-500 text-sm"></i>
                </div>
                {t("trust.secure")}
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground-600">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-accent-500 text-sm"></i>
                </div>
                {t("trust.free")}
              </div>
              <div className="flex items-center gap-2 text-xs text-foreground-600">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-accent-500 text-sm"></i>
                </div>
                {t("trust.gdpr")}
              </div>
            </div>
          </div>

          <div className="w-full lg:w-[35%] animate-fade-in-up animation-delay-200">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5]">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20lifestyle%20display%20with%20premium%20leather%20backpack%20and%20wool%20scarf%20on%20light%20oak%20table%2C%20soft%20diffused%20studio%20lighting%2C%20warm%20cream%20tone%20background%2C%20editorial%20product%20photography%2C%20rich%20textures%2C%20harmonious%20composition&width=800&height=1000&seq=hero-main-spreadsheet-product&orientation=portrait"
                alt="USFans Spreadsheet produkt"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
            </div>
          </div>

          <div className="w-full lg:w-[30%] flex flex-col gap-4 animate-fade-in-up animation-delay-400">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950">
                Polecane kategorie
              </h3>
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-primary-500"></i>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <a
                href="https://kaspreadsheet.com/category-911-Shoes.html"
                target="_blank"
                rel="noopener nofollow"
                className="col-span-2 relative rounded-xl overflow-hidden aspect-[2/1] group cursor-pointer"
              >
                <img
                  src="https://readdy.ai/api/search-image?query=Designer%20sneakers%20displayed%20on%20minimalist%20concrete%20surface%20with%20dried%20flowers%2C%20soft%20studio%20lighting%20from%20left%2C%20warm%20neutral%20beige%20background%2C%20contemporary%20fashion%20editorial%20photography&width=600&height=300&seq=hero-cat-sneakers-new&orientation=landscape"
                  alt="Buty"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-2 left-2 bg-background-50/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-foreground-950">
                  Shoes
                </div>
              </a>
              <a
                href="https://kaspreadsheet.com/category-912-Hoodies%2FSweaters.html"
                target="_blank"
                rel="noopener nofollow"
                className="relative rounded-xl overflow-hidden aspect-square group cursor-pointer"
              >
                <img
                  src="https://readdy.ai/api/search-image?query=Cashmere%20sweater%20draped%20artfully%20on%20light%20linen%20surface%2C%20minimalist%20studio%20photography%2C%20gentle%20natural%20light%2C%20warm%20sand%20tone%2C%20luxury%20fashion%20display%2C%20soft%20texture%20visible&width=400&height=400&seq=hero-cat-sweater-new&orientation=squarish"
                  alt="Hoodies"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-2 left-2 bg-background-50/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-foreground-950">
                  Hoodies
                </div>
              </a>
              <a
                href="https://kaspreadsheet.com/category-913-T-Shirts.html"
                target="_blank"
                rel="noopener nofollow"
                className="relative rounded-xl overflow-hidden aspect-square group cursor-pointer"
              >
                <img
                  src="https://readdy.ai/api/search-image?query=Organic%20cotton%20t-shirt%20neatly%20rolled%20on%20wooden%20surface%2C%20studio%20product%20photography%2C%20soft%20diffused%20natural%20lighting%2C%20cream%20background%2C%20modern%20sustainable%20fashion%20aesthetic%2C%20earthy%20tones&width=400&height=400&seq=hero-cat-shirt-new&orientation=squarish"
                  alt="T-Shirts"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-2 left-2 bg-background-50/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-foreground-950">
                  T-Shirts
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}